import {
  FieldWrapper,
  InputSelectionListChangeEvent,
  InputSelectionListProps,
} from '@dixa/lib-shared-habla';
import {
  HookFormFieldWrappedBaseProps,
  useHookFormFieldWrapper,
} from 'lib-shared-hook-form';
import type { Queue } from 'lib-shared-types-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FieldPath, FieldValues, useWatch } from 'react-hook-form';

import { queuesApi } from 'apis/queues';
import { InputQueuesSelect } from 'components/inputQueuesSelect';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

/**
 * Represents the React Hook Form variant of the {@link InputQueuesSelect}
 * component.
 *
 * @remarks
 *   The value is always an array, even when the select is not multiple. You need
 *   to get value[0] if you need a single queue id.
 */
export const HookFormQueuesSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: HookFormFieldWrappedBaseProps<
    TFieldValues,
    TName,
    Partial<InputSelectionListProps<Queue>>
  >,
): JSX.Element => {
  const dispatch = useAppDispatch();
  const { controller, fieldWrapperProps, controllerProps } =
    useHookFormFieldWrapper(props);
  const { control } = controllerProps;
  const value: string[] | undefined = useWatch({
    control,
    name: props.name,
  });
  const [selectedQueues, setSelectedQueues] = useState<Queue[]>([]);
  const setData = useCallback(
    (queues: Queue[]) => {
      setSelectedQueues(queues);
      controller.field.onChange(queues.map((queue) => queue.id));
    },
    [controller.field],
  );
  const setTimeoutResult = useRef<ReturnType<typeof setTimeout>>();

  const handleToggle = useCallback(
    (isOpen) => {
      if (!isOpen) {
        controller.field.onBlur();
      }
    },
    [controller.field],
  );

  const handleChange = useCallback(
    (event: InputSelectionListChangeEvent<Queue>) => {
      setData(event.selectedValues);
    },
    [setData],
  );

  const valueKey = JSON.stringify(value ?? []);

  useEffect(() => {
    const idsToQueues = async () => {
      if (!value?.length) {
        setSelectedQueues([]);
        return;
      }

      const responses = await Promise.all(
        value.map((id) => {
          const queue = selectedQueues.find((queue) => queue.id === id);
          if (queue) {
            return Promise.resolve({ data: queue });
          }

          return dispatch(
            queuesApi.endpoints.readQueue.initiate({ id }),
          ).unwrap();
        }),
      );

      setData(
        responses
          .filter((response) => response.data !== null)
          .map((response) => response.data),
      );
    };

    // There could be multiple re-renders, and there could be race condition on
    // what part of the field caused the update. Accordingly, sometimes, we'll
    // have a stale value. Since there is a network call to convert ids to
    // queues, we need to postpone the network call until the last re-render.
    // Hence, there is a setTimeout (debounce).
    clearTimeout(setTimeoutResult.current);
    setTimeoutResult.current = setTimeout(idsToQueues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  return (
    <FieldWrapper {...fieldWrapperProps}>
      <InputQueuesSelect
        value={selectedQueues}
        onChange={handleChange}
        onToggleDropdown={handleToggle}
        status={FieldWrapper.getStatus(fieldWrapperProps.meta)}
        {...props.componentProps}
      />
    </FieldWrapper>
  );
};
